import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore } from '@/stores/authStore';
import type { JobTitleDto } from '@/types/jobTitle';

import styles from '@features/departments/DepartmentModals.module.scss';

interface ViewJobTitleModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: JobTitleDto | null;
  onEdit: () => void;
  onDelete: () => void;
  onAudit?: () => void;
}

function StatusBadge({ status }: { status: JobTitleDto['status'] }) {
  const className = status === 'ACTIVE' ? styles.statusActive : styles.statusInactive;
  return <span className={`${styles.statusBadge} ${className}`}>{status}</span>;
}

function ViewJobTitleModal({
  isOpen,
  onClose,
  data,
  onEdit,
  onDelete,
  onAudit,
}: ViewJobTitleModalProps) {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (!data) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={data.name}
      size="md"
      footer={
        <div className={styles.viewFooter}>
          {hasPermission('JOB_TITLES_DELETE') && data.status === 'ACTIVE' && (
            <Button variant="danger" size="sm" onClick={onDelete}>
              {t('K_DEACTIVATE', 'Deactivate')}
            </Button>
          )}
          <div className={styles.viewFooterRight}>
            <Button variant="outline" size="sm" onClick={onClose}>
              {t('K_CLOSE', 'Close')}
            </Button>
            {onAudit && hasPermission('AUDIT_READ') && (
              <Button variant="outline" size="sm" onClick={onAudit}>
                {t('K_AUDIT_TITLE', 'Audit trail')}
              </Button>
            )}
            {hasPermission('JOB_TITLES_UPDATE') && data.status !== 'INACTIVE' && (
              <Button variant="filled" size="sm" onClick={onEdit}>
                {t('K_EDIT', 'Edit')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className={styles.fieldGrid}>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_NAME', 'Name')}</span>
          <span className={styles.fieldValue}>{data.name}</span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_CODE', 'Code')}</span>
          <span className={styles.fieldValue}>{data.code}</span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_STATUS', 'Status')}</span>
          <StatusBadge status={data.status} />
        </div>
        <div className={styles.fieldItem} style={{ gridColumn: '1 / -1' }}>
          <span className={styles.fieldLabel}>{t('K_LABEL_DEPARTMENTS', 'Departments')}</span>
          <div className={styles.deptChipRow}>
            {data.departments.length === 0 ? (
              <span className={styles.fieldValue}>—</span>
            ) : (
              data.departments.map((d) => (
                <span key={d.id} className={styles.deptChip} title={d.code}>
                  {d.name}
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default ViewJobTitleModal;
